import { useMemo } from 'react'
import type { Workspace } from '../../../../context/WorkspaceContext'
import type { WorkspaceActivityRecord } from '../../../../services/activityService'
import {
  auditDeletionTimestamp,
  auditRetentionMode,
} from '../../../../services/auditLedgerService'
import type { AnalysisRowResolution, AnalysisTimeRangeKey } from '../../../../types/analysis'
import type {
  LiveModuleHeadline,
  LiveModuleRecord,
  LiveModuleSparkline,
} from '../LiveModuleCard'
import { bucketize } from './bucketize'

const MS_PER_DAY = 86_400_000
const STANDARD_RETENTION_MS = 48 * 60 * 60 * 1000

export type UseRetentionStateModuleParams = {
  workspace: Workspace
  timeRange: AnalysisTimeRangeKey
  auditRecords: WorkspaceActivityRecord[]
  loading?: boolean
  error?: Error | null
  now?: Date
  resolutions?: Map<string, AnalysisRowResolution>
}

export const useRetentionStateModule = ({
  workspace,
  timeRange,
  auditRecords,
  loading = false,
  error = null,
  now = new Date(),
  resolutions,
}: UseRetentionStateModuleParams) => {
  return useMemo(() => {
    const mode = auditRetentionMode(workspace)
    const deletionTimestamp = auditDeletionTimestamp(workspace, now.getTime())

    // Earliest record timestamp
    const validRecords = auditRecords
      .filter((r) => r.createdAt instanceof Date && Number.isFinite(r.createdAt.getTime()))
      .sort((a, b) => (a.createdAt as Date).getTime() - (b.createdAt as Date).getTime())

    const earliestRecord = validRecords[0]?.createdAt as Date | undefined

    const hasRecords = validRecords.length > 0
    let daysRemaining: number | null = null
    if (mode === 'STANDARD' && earliestRecord) {
      const purgeBoundary = earliestRecord.getTime() + STANDARD_RETENTION_MS
      const diffMs = purgeBoundary - now.getTime()
      daysRemaining = Math.max(0, Math.ceil(diffMs / MS_PER_DAY))
    } else if (mode === 'FULL' && deletionTimestamp) {
      const diffMs = deletionTimestamp.toMillis() - now.getTime()
      daysRemaining = Math.max(0, Math.ceil(diffMs / MS_PER_DAY))
    }

    // Sparkline: distribution of record creation times over the window
    const timestamps = validRecords.map((r) => r.createdAt)
    const points = bucketize(timestamps, timeRange, now)

    // Top 5 oldest records approaching the retention purge boundary
    const records: LiveModuleRecord[] = validRecords.filter((record) => !resolutions?.has(`retention-state:${record.id}`)).slice(0, 5).map((r, index) => {
      const actor = r.actorName?.trim() || r.actorId || 'Member'
      const title = r.targetTitle?.trim() || r.action || r.activityType || 'Event'
      const ageDays = Math.max(0, Math.floor((now.getTime() - (r.createdAt as Date).getTime()) / MS_PER_DAY))
      return {
        id: r.id || `retention-${index}`,
        primary: title,
        secondary: `${actor} · ${ageDays}d old`,
        numeric: ageDays,
        drillTarget: 'SUMMARY',
      }
    })

    const headline: LiveModuleHeadline = !hasRecords
      ? { value: '—', label: mode === 'FULL' ? 'full retention' : 'no records' }
      : mode === 'FULL' && !deletionTimestamp
        ? { value: 'Full', label: 'retention' }
        : { value: daysRemaining ?? 0, label: 'days left' }

    const sparkline: LiveModuleSparkline = {
      points,
      direction: 'up-is-bad',
    }

    return {
      headline,
      sparkline,
      records,
      loading,
      error,
    }
  }, [workspace, auditRecords, timeRange, now, loading, error, resolutions])
}
